import { useLocation } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useCOAProcessing } from '@/contexts/COAProcessingContext';
import { useFeatureRegistration, type FeatureMeta } from '@/contexts/FeatureRegistryContext';

export const FEATURE_META: FeatureMeta = {
  id: 'coa-confirm-dialog',
  label: 'COA Strain Confirmation Dialog',
  description: 'Global dialog that asks the user to confirm applying COA lab data when the strain on the COA does not match the listing. Survives navigation away from the edit page.',
  triggerType: 'dialog',
  personas: ['seller', 'buyer_seller'],
};

/**
 * Mounted once at app root. Reads `pendingConfirm` from `COAProcessingContext`.
 * Auto-opens on the matching listing edit page; on any other route it stays
 * closed so the `COAProcessingBanner` "Review needed" CTA can guide the user
 * to the listing first.
 */
export default function COAConfirmDialog() {
  useFeatureRegistration(FEATURE_META);

  let ctx: ReturnType<typeof useCOAProcessing> | null = null;
  try {
    ctx = useCOAProcessing();
  } catch {
    return null;
  }

  const { pendingConfirm, confirmForce, declineForce } = ctx;
  const location = useLocation();

  if (!pendingConfirm) return null;

  // Only auto-open when the user is on the listing's edit page. Off-page
  // users see the yellow `COAProcessingBanner` and click "Review" (which
  // navigates here, where the dialog then auto-opens).
  const expectedPath = `/listings/${pendingConfirm.listingId}/edit`;
  const onEditPage = location.pathname === expectedPath;
  if (!onEditPage) return null;

  const { reason, detectedStrain, listingStrain } = pendingConfirm;

  const title =
    reason === 'mismatch' ? 'Strain name mismatch'
    : reason === 'low_confidence' ? "Couldn't confidently match strain"
    : reason === 'unreadable' ? "Couldn't read this PDF"
    : "Couldn't confirm strain name";

  const leadParagraph =
    reason === 'mismatch' ? 'This COA appears to be for a different strain than the listing.'
    : reason === 'low_confidence' ? "We couldn't confidently match this COA to the listing's strain."
    : reason === 'not_found' ? "We couldn't find this listing's strain referenced anywhere in the COA."
    : "This PDF has no readable text layer (it may be a scan), so the strain couldn't be verified.";

  return (
    <AlertDialog open onOpenChange={(open) => { if (!open) declineForce(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p className="text-destructive font-medium">{leadParagraph}</p>
              {reason !== 'unreadable' && (
                <div className="rounded border border-destructive/40 bg-destructive/5 p-3">
                  <ul className="space-y-1.5 text-sm">
                    <li className="flex items-center gap-2 text-destructive">
                      <span className="circle-1.5 bg-destructive flex-shrink-0" />
                      <span>Detected on COA: <strong>'{detectedStrain || 'Unknown'}'</strong></span>
                    </li>
                    <li className="flex items-center gap-2 text-destructive">
                      <span className="circle-1.5 bg-destructive flex-shrink-0" />
                      <span>This listing: <strong>'{listingStrain || 'Unknown'}'</strong></span>
                    </li>
                  </ul>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                The document is saved either way. If you continue, lab data will be applied to this listing —
                the strain name will <strong>not</strong> be changed.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => declineForce()}>Keep Document Only</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            onClick={() => { void confirmForce(); }}
          >
            Apply Lab Data Anyway
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}